<template>
  <ClientOnly>
    <PrimaryButton
      variant="secondary"
      data-target="Filters button"
      @on-click="drawerOpen = true"
    >
      <span class="flex items-center justify-center gap-1">
        <font-awesome-icon class="w-5 h-5" :icon="['fal', 'sliders']" />
        <p class="font-body-1 text-accent-700">{{ $t("common.filters") }}</p>
      </span>
    </PrimaryButton>
    <CustomDrawer
      :open="drawerOpen"
      :title="$t('appliedFilters.searchFilter')"
      @close="drawerOpen = false"
    >
      <template #header="{ close, titleId }">
        <span :id="titleId" class="font-subtitle-2-strong">
          {{ $t("appliedFilters.searchFilter") }}
        </span>
        <font-awesome-icon
          class="close-btn"
          :icon="['fal', 'times-circle']"
          @click="close"
        />
      </template>
      <div class="w-full h-full">
        <div class="px-[22px] pt-[6px] mb-4">
          <FilterSection
            v-if="!searchStore.onSearchPageCheck"
            :show-default-search-filter-button="
              !searchFilterStore.isCurrentFilterDefault
            "
            :is-always-expanded="!searchFilterStore.isCurrentFilterDefault"
          >
            <FilterNameSection @editing="onEdit" />
          </FilterSection>
          <FilterSection :title="$t('appliedFilters.mediaType')">
            <MediaTypeSection @perform-update="useSearchTask()" />
          </FilterSection>
          <FilterSection
            :title="$t('appliedFilters.mediaCategory')"
            :is-always-expanded="false"
            :is-expanded="isMediaTypeSectionExpanded"
            @toggle-expand="
              isMediaTypeSectionExpanded = !isMediaTypeSectionExpanded
            "
            @perform-update="useSearchTask()"
          >
            <MediaCategorySection @perform-update="useSearchTask()" />
          </FilterSection>
          <FilterSection
            :title="$t('appliedFilters.publicationRank')"
            :tooltip-text="publicationRankTooltipTextContent"
          >
            <PublicationReputationSection @perform-update="useSearchTask()" />
          </FilterSection>
          <FilterSection
            :title="$t('appliedFilters.articleRelevance')"
            :tooltip-text="articleRelevanceTooltipTextContent"
          >
            <ArticleRelevanceSection @perform-update="useSearchTask()" />
          </FilterSection>
          <FilterSection
            :title="$t('appliedFilters.mediaList')"
            :is-always-expanded="false"
            :is-expanded="isMediaListSectionExpanded"
            @toggle-expand="
              isMediaListSectionExpanded = !isMediaListSectionExpanded
            "
          >
            <MediaListSection />
          </FilterSection>
          <FilterSection :title="$t('appliedFilters.articleSentiment')">
            <ArticleSentimentSection @perform-update="useSearchTask()" />
          </FilterSection>
          <FilterSection
            :title="$t('appliedFilters.locationAndLanguage')"
            :is-always-expanded="false"
            :is-expanded="isLocationAndLanguageSectionExpanded"
            @toggle-expand="
              isLocationAndLanguageSectionExpanded =
                !isLocationAndLanguageSectionExpanded
            "
          >
            <LocationAndLanguageSection @perform-update="useSearchTask()" />
          </FilterSection>
          <FilterSection :title="$t('appliedFilters.exclusion')">
            <ExclusionSection @perform-update="useSearchTask()" />
          </FilterSection>
          <FilterSection
            v-show="showSavedFiltersSection"
            :title="$t('appliedFilters.savedFilters')"
          >
            <SavedFiltersSection />
          </FilterSection>
        </div>
        <AppliedFiltersButtons
          :is-editing="editingExistingSearchName"
          :search-save-disabled="searchSaveDisabled"
          @handle-save="$emit('handle-save')"
        />
      </div>
    </CustomDrawer>
  </ClientOnly>
</template>

<script setup lang="ts">
import { Ref, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import PrimaryButton from "../shared/primary-button/PrimaryButton.vue";
import FilterSection from "./FilterSection.vue";
import FilterNameSection from "./FilterNameSection.vue";
import MediaTypeSection from "./MediaTypeSection.vue";
import MediaCategorySection from "./MediaCategorySection.vue";
import PublicationReputationSection from "./PublicationReputationSection.vue";
import ArticleRelevanceSection from "./ArticleRelevanceSection.vue";
import ArticleSentimentSection from "./ArticleSentimentSection.vue";
import MediaListSection from "./MediaListSection.vue";
import LocationAndLanguageSection from "./LocationAndLanguageSection.vue";
import ExclusionSection from "./ExclusionSection.vue";
import SavedFiltersSection from "./SavedFiltersSection.vue";
import AppliedFiltersButtons from "./AppliedFiltersButtons.vue";
import CustomDrawer from "~/components/shared/custom-drawer/CustomDrawer.vue";
import { useSearchFiltersStore } from "~/store/searchFilter";
import { useSearchStore } from "~/store/search";
import { useSearchTask } from "~/composables/useSearchTask";
import { EXTERNAL_LINKS } from "~/constants/urls.ts";

defineEmits(["handle-save"]);

defineProps({
  searchSaveDisabled: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { PUBLICATION_RANK, RELEVANCE_SCORES } = EXTERNAL_LINKS;
const savedSearchStore = useSearchStore();
const searchFilterStore = useSearchFiltersStore();
const searchStore = useSearchStore();

const drawerOpen: Ref<boolean> = ref(false);
const editingExistingSearchName: Ref<boolean> = ref(false);
const isMediaTypeSectionExpanded: Ref<boolean> = ref(false);
const isMediaListSectionExpanded: Ref<boolean> = ref(false);
const isLocationAndLanguageSectionExpanded: Ref<boolean> = ref(false);

const publicationRankTooltipTextContent = `
  ${t("appliedFilters.publicationReputationTooltip")}
  <a
    href=${PUBLICATION_RANK}
    target="_blank"
    class="text-secondary-400"
    >
     ${t("linkTexts.learnMore")}
   </a>
  `;

const articleRelevanceTooltipTextContent = `
  ${t("appliedFilters.articleRelevanceTooltip")}
  <a
    href=${RELEVANCE_SCORES}
    target="_blank"
    class="text-secondary-400"
    >
     ${t("linkTexts.learnMore")}
   </a>
  `;

const showSavedFiltersSection = computed(() => {
  return !searchStore.onSearchPageCheck;
});

watch(
  () => savedSearchStore.isNotAccesptedSearch,
  (_isAccceptedSearch) => {
    if (_isAccceptedSearch) {
      drawerOpen.value = false;
      savedSearchStore.isNotAccesptedSearch = false;
    }
  },
);
const onEdit = (isEdit: Boolean) => {
  editingExistingSearchName.value = !!isEdit;
};
</script>

<style scoped>
.close-btn {
  @apply cursor-pointer text-[1.25rem] font-light not-italic hover:text-accent-800 text-accent-800;
}
</style>
